import classNames from "classnames";
import { ReactNode } from "react";
import { useTimezoneSelect } from "react-timezone-select";
import { CreatedEvent } from "../../redux/features/event/event.types";
import { eventStatusPillMap, tzOptions } from "../../utils/constants";
import { formatDate } from "../../utils/helpers";
import { Icon } from "../icon/icon";
import { Pill } from "../pill";
import { BgWrapper } from "../wrapper/bgWrapper";
import { EventImage } from "./eventImage";

export const EventCard = ({
  image,
  name,
  location,
  startDate,
  endDate,
  startTime,
  endTime,
  noTickets,
  totalGuests,
  isOrganizer,
  event,
  children,
  className,
  imageDimension = 300,
  imageClassName = "",
  titleClassName = "",
  clickable = false,
  onClick = () => {},
  hasPaidTickets = false,
  showUserTypePill = true,
  showStatusPill = false,
  showLocationPill = false,
  showTicketPill = false,
  showCreatedOn = false,
  hasPaidTicketsType = false,
}: {
  image: string;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  noTickets?: number;
  totalGuests?: number;
  isOrganizer: boolean;
  event?: CreatedEvent;
  children?: ReactNode;
  className?: string;
  imageDimension?: number;
  imageClassName?: string;
  titleClassName?: string;
  clickable?: boolean;
  onClick?: () => void;
  hasPaidTickets?: boolean;
  showUserTypePill?: boolean;
  showStatusPill?: boolean;
  showLocationPill?: boolean;
  showTicketPill?: boolean;
  showCreatedOn?: boolean;
  hasPaidTicketsType?: boolean;
}) => {
  const { parseTimezone } = useTimezoneSelect(tzOptions);

  return (
    <BgWrapper
      className={classNames(
        `flex flex-col gap-8 md:flex-row md:flex-wrap md:items-center`,
        {
          "!cursor-pointer": clickable,
        },
        className,
      )}
      onClick={onClick}
    >
      <EventImage
        image={image}
        dimension={imageDimension}
        className={imageClassName}
      />

      <div
        className={classNames(
          "flex w-full flex-col justify-center gap-4 md:min-w-[350px] md:flex-1 xl:!min-h-full xl:w-fit",
          {
            "md:min-h-[200px]": imageDimension === 200,
            "md:min-h-[300px]": imageDimension === 300,
            "md:min-h-[400px]": imageDimension === 400,
          },
        )}
      >
        <div className="flex items-center gap-2">
          {showUserTypePill && (
            <Pill
              className=""
              text={isOrganizer ? "Organizer" : "Guest"}
              variation={isOrganizer ? "purple" : "grey"}
            />
          )}

          {(showStatusPill || event?.status === "draft") && (
            <Pill
              className=""
              text={eventStatusPillMap[event?.status!]?.text}
              variation={eventStatusPillMap[event?.status!]?.variant}
            />
          )}

          {showLocationPill && (
            <Pill
              className=""
              text={event?.type === "physical" ? "PHYSICAL" : "VIRTUAL"}
              variation={event?.type === "physical" ? "blue" : "blue"}
            />
          )}
          {showTicketPill && (
            <Pill
              className=""
              text={
                hasPaidTicketsType
                  ? "PAID"
                  : hasPaidTickets
                    ? "PAID TICKET"
                    : "FREE"
              }
              variation={hasPaidTicketsType || hasPaidTickets ? "green" : "grey"}
            />
          )}
        </div>

        <p
          className={classNames(
            "text-32 flex flex-col gap-1 font-fraunces font-medium",
            titleClassName,
          )}
        >
          {name}
          {showCreatedOn && (
            <span className="font-fustat text-sm font-normal tracking-normal text-grey-900">
              Created On {formatDate(event?.createdAt, "ddd, MMMM DD, YYYY")}
            </span>
          )}
        </p>

        <div className="flex flex-col gap-[10px] text-sm text-grey-900">
          <p className="flex items-center gap-2">
            <Icon
              svg="map-pin"
              width={20}
              height={20}
              className="min-w-fit text-grey-500"
            />
            {location}
          </p>

          <p className="flex items-center gap-2">
            <Icon
              svg="calendar"
              width={20}
              height={20}
              className="min-w-fit text-grey-500"
            />
            {startDate} {startDate === endDate ? "" : `- ${endDate}`}
          </p>

          <p className="flex items-center gap-2">
            <Icon
              svg="clock"
              width={20}
              height={20}
              className="min-w-fit text-grey-500"
            />
            {startTime} - {endTime}{" "}
            {event?.timezone
              ? `(${parseTimezone(event.timezone)?.abbrev})`
              : ""}
          </p>

          {noTickets !== undefined && !isOrganizer ? (
            <p className="flex items-center gap-2">
              <Icon
                svg="ticket"
                width={20}
                height={20}
                className="min-w-fit text-grey-500"
              />
              {noTickets} {noTickets === 1 ? "Ticket" : "Tickets"}
            </p>
          ) : undefined}

          {isOrganizer ? (
            <p className="flex items-center gap-2">
              <Icon
                svg="users"
                width={20}
                height={20}
                className="min-w-fit text-grey-500"
              />
              {(totalGuests ?? 0) > 0 ? totalGuests : "No"}{" "}
              {(totalGuests ?? 0) === 1 ? "Guest" : "Guests"}
            </p>
          ) : undefined}
        </div>

        {children}
      </div>
    </BgWrapper>
  );
};

export const EventCardSkeleton = ({
  className,
  imageDimension = 300,
  showExtraDetails = false,
}: {
  className?: string;
  imageDimension?: number;
  showExtraDetails?: boolean;
}) => {
  return (
    <BgWrapper
      className={`flex flex-col gap-8 md:flex-row md:items-center ${className}`}
    >
      <div
        className={classNames(
          "w-full animate-pulse rounded-[14px] bg-grey-50",
          {
            "h-[300px] md:max-w-[300px]": imageDimension === 300,
            "h-[300px] md:h-[400px] md:max-w-[400px]": imageDimension === 400,
          },
        )}
      />

      <div className="flex h-full flex-1 flex-col gap-4">
        <div className="h-6 w-20 animate-pulse rounded-xl bg-grey-50" />

        <div className="h-8 w-48 animate-pulse rounded-xl bg-grey-50" />

        <div className="flex flex-col gap-[10px]">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              className="flex items-center gap-2"
              key={`skeletion-sub-details-${index}`}
            >
              <div className="h-5 w-5 animate-pulse rounded-xl bg-grey-50" />
              <div className="h-4 w-32 animate-pulse rounded-xl bg-grey-50" />
            </div>
          ))}
        </div>

        {showExtraDetails && (
          <div className="flex flex-col gap-2">
            <div className="h-8 w-full animate-pulse rounded-xl bg-grey-50" />
            <div className="h-8 w-11/12 animate-pulse rounded-xl bg-grey-50" />
            <div className="h-8 w-10/12 animate-pulse rounded-xl bg-grey-50" />
          </div>
        )}
      </div>
    </BgWrapper>
  );
};
